import {collectVisibleText} from './visibleText'

/**
 * look_at_screen's browser half (AB#6762, layer 2): an image of the whole app
 * viewport as the user sees it (top bar, nav rail, page, and the Alpinist panel),
 * plus the visible text of everything but the Alpinist panel. The panel's text
 * is the conversation, which Alpinist already has.
 *
 * A DOM-to-image library REDRAWS the page (it clones the DOM into an SVG
 * foreignObject and rasterizes that); it is not a real screenshot.
 * `modern-screenshot` was chosen over `html-to-image` by comparing both with real
 * screenshots of the v1 pages in Chromium, Chrome, Edge and WebKit, light and
 * dark (PR for AB#6762): both are faithful, but html-to-image took up to 13 s on
 * the Signal Explorer, against the backend's 15 s wait.
 */

export const CAPTURE_TARGET_WIDTH = 1600
export const CAPTURE_MAX_BYTES = 500 * 1024
export const CAPTURE_MAX_TEXT = 8000
// Below this the text in a dense table stops being legible to the model.
const MIN_QUALITY = 0.45
const MAX_SCALE = 2

/** Nodes marked with this attribute are left out of the image and the text. */
export const CAPTURE_EXCLUDE_ATTR = 'data-alpinist-capture-exclude'
/** The Alpinist side panel: in the image, but its text is left out. */
export const ALPINIST_PANEL_ATTR = 'data-alpinist-panel'

export type ScreenCapture = {
  image: Blob
  width: number
  height: number
  text: string
  /** Capture time in ms, for the logs. */
  ms: number
}

const isExcluded = (node: Node): boolean =>
  node instanceof Element && node.hasAttribute(CAPTURE_EXCLUDE_ATTR)

// Not on screen, but the clone would draw them: <noscript>'s "enable JavaScript"
// line across the top, and the library's own hidden sandbox iframe.
const isNeverDrawn = (node: Node): boolean =>
  node instanceof Element &&
  (node.tagName === 'NOSCRIPT' || (node.tagName === 'IFRAME' && node.id.startsWith('__SANDBOX__')))

export const isExcludedFromText = (node: Node): boolean =>
  isExcluded(node) || (node instanceof Element && node.hasAttribute(ALPINIST_PANEL_ATTR))

const toCanvas = async (
  el: HTMLElement,
  scale: number,
  backgroundColor: string,
): Promise<HTMLCanvasElement> => {
  // Loaded on the first capture only: most sessions never look.
  const {domToCanvas} = await import('modern-screenshot')
  return domToCanvas(el, {
    scale,
    backgroundColor,
    filter: (node: Node): boolean => !isExcluded(node) && !isNeverDrawn(node),
    // Off by default: without it every scrolled container (the chat, a long
    // dashboard, a table) is drawn from its top, not where the user is.
    features: {restoreScrollPosition: true},
    // Before cloning, the library waits for every <img>/<video> to load, each up
    // to this long. A loaded page captures in ~0.2 s, but one image still
    // loading held every capture for the full timeout (3.3 s measured with
    // 3000). It is drawn blank instead: better than a capture that misses the
    // backend's wait on a slow connection.
    timeout: 1500,
  })
}

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null'))),
      type,
      quality,
    )
  })

/**
 * Encode under the byte cap: WebP where the browser encodes it (Safari's canvas
 * does not, and silently hands back a PNG), JPEG otherwise, stepping the quality
 * down and then the size until it fits.
 */
export const encodeUnderCap = async (
  source: HTMLCanvasElement,
  maxBytes = CAPTURE_MAX_BYTES,
): Promise<{blob: Blob; width: number; height: number}> => {
  let canvas = source
  for (;;) {
    for (const type of ['image/webp', 'image/jpeg']) {
      for (let quality = 0.85; quality >= MIN_QUALITY; quality -= 0.1) {
        const blob = await canvasToBlob(canvas, type, quality)
        if (blob.type !== type) break // the browser cannot encode this type
        if (blob.size <= maxBytes) return {blob, width: canvas.width, height: canvas.height}
      }
    }
    if (canvas.width < 400) {
      throw new Error('capture does not fit the size limit')
    }
    const smaller = document.createElement('canvas')
    smaller.width = Math.round(canvas.width * 0.75)
    smaller.height = Math.round(canvas.height * 0.75)
    smaller.getContext('2d')?.drawImage(canvas, 0, 0, smaller.width, smaller.height)
    canvas = smaller
  }
}

const pageBackground = (el: HTMLElement): string => {
  for (let node: HTMLElement | null = el; node; node = node.parentElement) {
    const color = getComputedStyle(node).backgroundColor
    if (color && color !== 'transparent' && !/rgba\(.*,\s*0\)$/.test(color)) return color
  }
  return '#ffffff'
}

/**
 * Capture the app as it is on screen now. `root` defaults to the document body,
 * which the app shell pins to the viewport (100dvh, no page scroll), so the
 * image is the viewport, dialogs and menus in their portals included.
 */
export const captureScreen = async (root: HTMLElement = document.body): Promise<ScreenCapture> => {
  const started = performance.now()
  const width = Math.max(1, root.clientWidth)
  const scale = Math.min(MAX_SCALE, CAPTURE_TARGET_WIDTH / width)
  // Text first: the clone the library makes is not laid out, so the live DOM is
  // the only place visibility can be read.
  const text = collectVisibleText(root, {
    maxChars: CAPTURE_MAX_TEXT,
    isExcluded: isExcludedFromText,
  })
  const canvas = await toCanvas(root, scale, pageBackground(root))
  const encoded = await encodeUnderCap(canvas)
  return {
    image: encoded.blob,
    width: encoded.width,
    height: encoded.height,
    text,
    ms: Math.round(performance.now() - started),
  }
}
